/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "C# SDK", description = "Hightouch's C# SDK for collecting events", lastUpdatedDate = new Date(1724112000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "C# SDK",
        description: "Hightouch's C# SDK for collecting events",
        lastUpdatedDate: "Tue Aug 20 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      pre: "pre",
      code: "code",
      span: "span",
      h3: "h3",
      strong: "strong"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The C# SDK makes it easy to track events from all C#/.NET applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The C# SDK can be installed from ", _jsx(_components.a, {
          href: "https://www.nuget.org/packages/Hightouch.Events.CSharp",
          children: "NuGet"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-sh",
          children: "dotnet add package Hightouch.Events.CSharp\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To initialize the C# SDK in your application, create a new ", _jsx(_components.code, {
          children: "Analytics"
        }), " instance:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-c#",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "using"
          }), " Hightouch.Events;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "using"
          }), " Hightouch.Events.Serialization;\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " configuration = ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Configuration(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"WRITE_KEY\""
          }), ",\n  apiHost: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"https://us-east-1.hightouch-events.com\""
          }), ",\n  flushAt: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ");\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " analytics = ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Analytics(configuration);\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.Identify"
        }), " method sends an ", _jsx(_components.a, {
          href: "../event-spec#identify-events",
          children: _jsx(_components.code, {
            children: "identify"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-c#",
          children: ["analytics.Identify(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user-123\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " JsonObject {\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"username\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"MisterWhiskers\""
          }), ",\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"hello@test.com\""
          }), ",\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"plan\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"premium\""
          }), "\n});\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.Track"
        }), " method sends a ", _jsx(_components.a, {
          href: "../event-spec#track-events",
          children: _jsx(_components.code, {
            children: "track"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-c#",
          children: ["analytics.Track(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"View Product\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " JsonObject {\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"productId\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123"
          }), ",\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"productName\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Striped trousers\""
          }), "\n});\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.Page"
        }), " method sends a ", _jsx(_components.a, {
          href: "../event-spec#page-events",
          children: _jsx(_components.code, {
            children: "page"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-c#",
          children: ["analytics.Page(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"PageName\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " JsonObject {\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"productSlug\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"example-product-123\""
          }), "\n});\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.Screen"
        }), " method sends a ", _jsx(_components.a, {
          href: "../event-spec#screen-events",
          children: _jsx(_components.code, {
            children: "screen"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-c#",
          children: ["analytics.Screen(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ScreenName\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " JsonObject {\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"productSlug\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"example-product-123\""
          }), "\n});\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.Group"
        }), " method sends a ", _jsx(_components.a, {
          href: "../event-spec#group-events",
          children: _jsx(_components.code, {
            children: "group"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-c#",
          children: ["analytics.Group(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user-123\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " JsonObject {\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"username\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"MisterWhiskers\""
          }), ",\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"hello@test.com\""
          }), ",\n    [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"plan\""
          }), "] = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"premium\""
          }), "\n});\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The C# SDK buffers events locally, either in-memory or on disk, before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking non-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "Flush()"
        }), "\nmethod. ", _jsx(_components.code, {
          children: "Flush()"
        }), " should be called when shutting down your app to make sure no events are lost."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-c#",
          children: "analytics.Flush()\n"
        })
      })]
    });
  }
}
export default MDXContent;
